import * as React from "react";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {isTouchDevice} from "../../helpers/deviceInformation";
import {cdnDomain} from "../../helpers/cnxDomains";
import {getShopId} from "../lib/getShopId";
import {Tracking} from "../../tracking/tracking";
import window from "../lib/WindowExtended";
import styles from "./GiftBlock.module.scss";

/**
 * Option to print gifts with delivery delay as PDF gift card ("#ProductGiftBlock")
 */

type GiftBlockProps = {
  productTitle: string;
  productImage: string;
};
const GiftBlock: React.FC<GiftBlockProps> = ({productTitle, productImage}) => {
  const {t} = useTranslation();
  const [showInfo, setShowInfo] = useState<boolean>(false);

  const deGiftCardName = "Connox-Geschenkkarte-zum-Ausdrucken-DE.jpg";
  const enGiftCardName = "Printable-Connox-Gift-Card-EN.jpg";
  const frGiftCardName = "Carte-cadeau-Connox-a-imprimer-FR.jpg";
  const daGiftCardName = "Connox-gavekort-til-print-DA.jpg";
  const nlGiftCardName = "Connox-cadeaubon-om-te-printen-NL.jpg";

  const giftCardFileName: {[key: number]: string} = {
    100001: deGiftCardName,
    100009: deGiftCardName,
    100017: deGiftCardName,
    100027: frGiftCardName,
    100008: frGiftCardName,
    100005: enGiftCardName,
    100020: enGiftCardName,
    100022: daGiftCardName,
    100029: nlGiftCardName,
  };

  const imgRegExp = productImage?.match("/media/.+.jpg");
  const productImgUrl = imgRegExp ? cdnDomain[getShopId()] + imgRegExp[0] : productImage;
  const baseImgUrl = cdnDomain[getShopId()] + "/media/Gutscheine/" + giftCardFileName[getShopId()];

  const printFunction = () => {
    const printBlock = document.getElementById("GiftBlockPrintArea") as HTMLDivElement;
    const printTitle = "connox-" + t("product.giftBlock.printTitle");
    const printContent = printBlock.innerHTML;
    const printContainer = window.open(" ", "");
    const lang = document.body.dataset.lang ?? "en";

    if (printContainer && baseImgUrl && productImgUrl) {
      printContainer.document.write(
        `<html lang=${lang}><head><title>${printTitle}</title></head><body>${printContent}</body></html>`,
      );
      printContainer.document.close();
      printContainer.print();
    }

    Tracking.sendEvent("GiftBlock.PrintLink.Clicked");
  };

  const openInfo = () => {
    setShowInfo(true);
  };

  const closeInfo = () => {
    setShowInfo(false);
  };

  const GiftCardInfoText: React.FC = () => {
    return (
      <div className={styles.info}>
        <div className={styles.infoHead}>
          <p>{t("product.giftBlock.infoTitle")}</p>
          {isTouchDevice() && (
            <span className={styles.infoClose} role="link" onClick={closeInfo}>
              <i className="icon icon-size--14 icon-close" />
            </span>
          )}
        </div>
        <p>{t("product.giftBlock.infoText")}</p>
      </div>
    );
  };

  return (
    <>
      <span className={styles.link}>
        <i className="icon icon-size--24 icon-gift" />
        <span className={`${styles.linkText} link`} role="link" onClick={printFunction}>
          {t("product.giftBlock.linkTitle")}
        </span>
        <span className={styles.linkInfo} role="link" onMouseEnter={openInfo} onMouseLeave={closeInfo}>
          <i className="icon icon-size--18 icon-info--yellow" />
        </span>
        {showInfo && <GiftCardInfoText />}
      </span>
      <div className={styles.printArea} id="GiftBlockPrintArea">
        <div style={{position: "relative", width: "1060px"}}>
          <img src={baseImgUrl} alt={t("product.giftBlock.printTitle")} width={"1060"} />
          <img
            style={{
              position: "absolute",
              top: "130px",
              right: "85px",
              mixBlendMode: "multiply",
            }}
            src={productImgUrl}
            alt={productTitle}
            width={"400"}
          />
          <p
            style={{
              position: "absolute",
              bottom: "26px",
              left: "50px",
              margin: "0",
              fontSize: "28px",
              fontFamily: "sans-serif",
              maxWidth: "800px",
              minHeight: "48px",
              maxHeight: "65px",
              overflow: "hidden",
            }}
          >
            {productTitle}
          </p>
        </div>
      </div>
    </>
  );
};

export default GiftBlock;
